import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Calendar } from "lucide-react";
import Navbar from "@/components/Navbar";
import Seo from "@/components/Seo";
import { BLOG } from "@/data/blog";
import { CONTACT } from "@/data/content";

const BlogPost = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const post = BLOG[slug];

  if (!post) {
    return (
      <main className="min-h-screen bg-stone-50 text-stone-900">
        <Navbar />
        <div className="max-w-3xl mx-auto px-6 pt-40 pb-24 text-center">
          <h1 className="font-heading text-4xl font-medium">Articolo non trovato</h1>
          <button data-testid="back-home-button" onClick={() => navigate("/")} className="inline-flex items-center gap-2 mt-6 rounded-full bg-lime-900 text-lime-50 px-6 py-3 font-medium">
            <ArrowLeft size={18} /> Torna alla home
          </button>
        </div>
      </main>
    );
  }

  return (
    <main className="bg-stone-50 text-stone-900">
      <Seo
        title={`${post.title} | A.S.I. Sicilia ETS`}
        description={post.intro.slice(0, 160)}
        path={`/blog/${slug}`}
        image={post.img}
        type="article"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.intro,
          image: `https://asisiciliaets.it${post.img}`,
          datePublished: `${post.date}-01-01`,
          author: { "@type": "Organization", name: "A.S.I. Sicilia ETS" },
          publisher: {
            "@type": "Organization",
            name: "A.S.I. Sicilia ETS",
            logo: { "@type": "ImageObject", url: "https://asisiciliaets.it/assets/logo.png" },
          },
          mainEntityOfPage: `https://asisiciliaets.it/blog/${slug}`,
        }}
      />
      <Navbar />

      <article className="pt-32 pb-24">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/#blog" target="_blank" rel="noopener noreferrer" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-sm font-medium text-stone-500 hover:text-lime-900 transition-colors">
            <ArrowLeft size={16} /> Torna al blog
          </Link>

          <div className="flex items-center gap-3 text-xs mt-6">
            <span className="rounded-full bg-[#E2725B]/10 text-[#c2543e] px-3 py-1 font-semibold uppercase tracking-wide">{post.tag}</span>
            <span className="text-stone-400 font-mono inline-flex items-center gap-1"><Calendar size={13} /> {post.date}</span>
          </div>

          <h1 className="font-heading text-4xl lg:text-5xl font-medium tracking-tight mt-4 leading-tight">{post.title}</h1>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
          <img src={post.img} alt={post.title} className="w-full h-64 sm:h-96 object-cover rounded-2xl" />
        </div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 mt-10">
          <p className="font-heading text-xl sm:text-2xl text-stone-700 leading-relaxed">{post.intro}</p>

          {post.sections.map((s, i) => (
            <section key={i} className="mt-10">
              <h2 className="font-heading text-2xl sm:text-3xl font-medium">{s.h}</h2>
              {s.p.map((para, j) => (
                <p key={j} className="text-stone-600 text-lg leading-relaxed mt-4">{para}</p>
              ))}
            </section>
          ))}

          {/* CTA */}
          <div className="mt-14 rounded-2xl bg-lime-950 text-stone-100 p-8 sm:p-10">
            <h3 className="font-heading text-2xl sm:text-3xl font-medium text-white">Hai bisogno di aiuto?</h3>
            <p className="text-stone-300 mt-3 leading-relaxed">
              A.S.I. Sicilia ETS affianca gratuitamente persone e famiglie. Contattaci: ti ascoltiamo e ti orientiamo.
            </p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/#contatti" target="_blank" rel="noopener noreferrer" data-testid="article-cta-contatti" className="inline-flex items-center gap-2 rounded-full bg-[#E2725B] text-white px-6 py-3 font-semibold hover:bg-[#d5604a] transition-colors">
                Chiedi aiuto <ArrowRight size={18} />
              </Link>
              <Link to="/#sostienici" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full bg-white/10 text-white border border-white/30 px-6 py-3 font-medium hover:bg-white/20 transition-colors">
                Sostienici
              </Link>
            </div>
            <p className="text-stone-400 text-sm mt-5">Tel. {CONTACT.tel} · {CONTACT.email}</p>
          </div>
        </div>
      </article>

      <footer className="bg-lime-950 text-stone-400 py-10 border-t border-lime-900/60">
        <div className="max-w-7xl mx-auto px-6 text-sm text-center">
          © {new Date().getFullYear()} A.S.I. Sicilia ETS · C.F. {CONTACT.cf}
        </div>
      </footer>
    </main>
  );
};

export default BlogPost;
